'use client';

import { IBlogArticleListItem } from '@/api/types/blog';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { scrollToAnchor } from '@/utils/tools';
import React, {
  ReactNode,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ArticleCard from './ArticleCard';
import BlogNoArticle from './BlogNoArticle';

interface ArticleListProps {
  title?: string;
  articles: ArticleListSlot[];
  page: number;
  total: number;
  loadedCount?: number;
  loading?: boolean;
  listExp?: (article: IBlogArticleListItem, index: number) => void;
  listItemClick?: (
    article: IBlogArticleListItem,
    index: number,
    clickArea: 'article' | 'author',
  ) => void;
  onPageChange?: (page: number) => void;
  onLoadMore?: (targetLoadedCount?: number) => void;
  titleComponent?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
  titleClassName?: string;
  listClassName?: string;
  itemClassName?: string;
  articleClassName?: string;
  emptyComponent?: ReactNode;
  useNativeArticleImage?: boolean;
  virtualized?: boolean;
  virtualOverscan?: number;
  onVirtualRangeChange?: (range: ArticleListVirtualRange) => void;
}

type ArticleListSlot = IBlogArticleListItem | null | undefined;

interface ArticleListVirtualRange {
  startIndex: number;
  endIndex: number;
  visibleStartIndex: number;
  visibleEndIndex: number;
  pageSize: number;
  total: number;
}

interface VirtualWindow {
  columns: number;
  endRow: number;
  endIndex: number;
  itemHeight: number;
  rowGap: number;
  rowStride: number;
  startRow: number;
  startIndex: number;
  totalHeight: number;
  topSpacer: number;
  bottomSpacer: number;
  visibleStartIndex: number;
  visibleEndIndex: number;
}

interface VirtualMetrics {
  columns: number;
  itemHeight: number;
  listTop: number;
  rowGap: number;
}

const VIRTUALIZATION_MIN_COUNT = 40;
const VIRTUAL_SCROLL_UPDATE_INTERVAL = 24;
const VIRTUAL_BUFFER_ROW_MULTIPLIER = 3;
const VIRTUAL_PRUNE_ROW_MULTIPLIER = 2;
const VIRTUAL_FORWARD_BUFFER_MULTIPLIER = 2;

interface ArticleListItemProps {
  as?: 'div' | 'li';
  article: IBlogArticleListItem;
  articleClassName: string;
  index: number;
  isWeb: boolean;
  itemClassName: string;
  listExp: (article: IBlogArticleListItem, index: number) => void;
  listItemClick: (
    article: IBlogArticleListItem,
    index: number,
    clickArea: 'article' | 'author',
  ) => void;
  useNativeArticleImage: boolean;
}

const ArticleListItem = memo(function ArticleListItem({
  as: Root = 'li',
  article,
  articleClassName,
  index,
  isWeb,
  itemClassName,
  listExp,
  listItemClick,
  useNativeArticleImage,
}: ArticleListItemProps) {
  const handleArticleExp = useCallback(() => {
    listExp(article, index);
  }, [article, index, listExp]);

  const handleArticleClick = useCallback(() => {
    listItemClick(article, index, 'article');
  }, [article, index, listItemClick]);

  const handleAuthorClick = useCallback(() => {
    listItemClick(article, index, 'author');
  }, [article, index, listItemClick]);

  const normalizedArticleClassName =
    !isWeb && articleClassName.trim().length === 0 ? '!mb-0' : articleClassName;

  return (
    <Root
      data-article-item="true"
      className={`${isWeb ? '' : 'not-first:mt-[64px]'} ${itemClassName}`}
      style={{
        contentVisibility: 'auto',
        containIntrinsicSize: isWeb ? '520px' : '430px',
      }}
    >
      <ArticleCard
        article={article}
        className={normalizedArticleClassName}
        showIntro
        onArticleExp={handleArticleExp}
        onArticleClick={handleArticleClick}
        onAuthorClick={handleAuthorClick}
        useNativeImage={useNativeArticleImage}
      />
    </Root>
  );
});

const ArticleList: React.FC<ArticleListProps> = ({
  title = '',
  articles,
  page,
  total,
  loading = false,
  listExp = () => {},
  listItemClick = () => {},
  onPageChange,
  onLoadMore,
  titleComponent = 'h4',
  className = '',
  titleClassName = '',
  listClassName = '',
  itemClassName = '',
  articleClassName = '',
  emptyComponent,
  useNativeArticleImage = false,
  virtualized = false,
  virtualOverscan = 6,
  loadedCount,
  onVirtualRangeChange,
}) => {
  const [limit] = useState<number>(18);
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const listRef = useRef<HTMLUListElement | null>(null);
  const virtualFrameRef = useRef<number | null>(null);
  const virtualDelayTimerRef = useRef<number | null>(null);
  const virtualLastUpdateRef = useRef(0);
  const virtualMetricsRef = useRef<VirtualMetrics>({
    columns: 1,
    itemHeight: 420,
    listTop: 0,
    rowGap: 0,
  });
  const [virtualWindow, setVirtualWindow] = useState<VirtualWindow>({
    columns: 1,
    endRow: 0,
    endIndex: 0,
    itemHeight: 420,
    rowGap: 0,
    rowStride: 420,
    startRow: 0,
    startIndex: 0,
    totalHeight: 0,
    topSpacer: 0,
    bottomSpacer: 0,
    visibleStartIndex: 0,
    visibleEndIndex: 0,
  });
  const articleCount = Array.isArray(articles) ? articles.length : 0;
  const loadedArticleCount = loadedCount ?? articleCount;
  const virtualItemCount = !isWeb && total > articleCount ? total : articleCount;
  const shouldVirtualize = virtualized && virtualItemCount > VIRTUALIZATION_MIN_COUNT;

  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);

  // 处理滚动到底部
  const handleReachBottom = () => {
    setIsShowFooterNav(false);
    onLoadMore?.();
  };

  // 处理分页变化
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    logger.log('handlePageChange', value);
    // 状态更新后延迟执行滚动，确保DOM更新完成
    setTimeout(() => {
      scrollToAnchor('#article-list', isWeb ? 100 : 45);
    }, 0);
    onPageChange?.(value);
  };

  useEffect(() => {
    if (isWeb) return;
    if (Array.isArray(articles) && loadedArticleCount >= total) {
      setIsShowFooterNav(true);
    } else {
      setIsShowFooterNav(false);
    }
  }, [articles, isWeb, loadedArticleCount, setIsShowFooterNav, total]);

  const updateVirtualMetrics = useCallback(() => {
    const listElement = listRef.current;
    const listWidth = listElement?.getBoundingClientRect().width || 0;
    const columns = isWeb ? (window.innerWidth <= 960 ? 1 : window.innerWidth <= 1540 ? 2 : 3) : 1;
    const rowGap = isWeb ? 64 : 0;
    const columnGap = isWeb ? 24 : 0;
    const estimatedWidth =
      listWidth > 0 ? (listWidth - columnGap * (columns - 1)) / columns : window.innerWidth - 28;
    virtualMetricsRef.current = {
      columns,
      itemHeight: estimatedWidth * (9 / 16) + (isWeb ? 235 : 251),
      listTop: listElement ? listElement.getBoundingClientRect().top + window.scrollY : 0,
      rowGap,
    };
  }, [isWeb]);

  const updateVirtualWindow = useCallback(() => {
    if (!shouldVirtualize || virtualItemCount === 0) return;

    const listElement = listRef.current;
    if (!listElement) return;

    const { columns, itemHeight, listTop, rowGap } = virtualMetricsRef.current;
    const rowCount = Math.ceil(virtualItemCount / columns);
    const rowStride = itemHeight + rowGap;
    const visibleTop = Math.max(0, window.scrollY - listTop);
    const visibleBottom = Math.max(0, window.scrollY + (window.innerHeight || 0) - listTop);
    const visibleRowCount = Math.max(1, Math.ceil((window.innerHeight || 0) / rowStride) + 1);
    const visibleStartRow = Math.max(0, Math.floor(visibleTop / rowStride));
    const visibleEndRow = Math.min(rowCount, Math.ceil(visibleBottom / rowStride));
    const bufferRows = Math.max(virtualOverscan * VIRTUAL_BUFFER_ROW_MULTIPLIER, visibleRowCount);
    const forwardBufferRows = bufferRows * VIRTUAL_FORWARD_BUFFER_MULTIPLIER;
    const pruneRows = Math.max(
      forwardBufferRows * VIRTUAL_PRUNE_ROW_MULTIPLIER,
      visibleRowCount * 2,
    );
    const desiredStartRow = Math.max(0, visibleStartRow - bufferRows);
    const desiredEndRow = Math.min(rowCount, visibleEndRow + forwardBufferRows);

    setVirtualWindow((prevWindow) => {
      const previousStartRow = Math.floor(prevWindow.startIndex / columns);
      const previousEndRow =
        prevWindow.endIndex > 0 ? Math.ceil(prevWindow.endIndex / columns) : desiredEndRow;
      let startRow = previousStartRow;
      let endRow = previousEndRow;

      if (prevWindow.columns !== columns || prevWindow.endIndex === 0) {
        startRow = desiredStartRow;
        endRow = desiredEndRow;
      } else {
        if (visibleStartRow <= previousStartRow + virtualOverscan) {
          startRow = desiredStartRow;
        }
        if (visibleEndRow >= previousEndRow - virtualOverscan) {
          endRow = desiredEndRow;
        }
        if (visibleStartRow - startRow > pruneRows) {
          startRow = desiredStartRow;
        }
        if (endRow - visibleEndRow > pruneRows) {
          endRow = desiredEndRow;
        }
      }

      startRow = Math.max(0, Math.min(startRow, rowCount - 1));
      endRow = Math.max(startRow + 1, Math.min(endRow, rowCount));

      const startIndex = startRow * columns;
      const endIndex = Math.min(virtualItemCount, endRow * columns);
      const visibleStartIndex = Math.max(
        0,
        Math.min(virtualItemCount - 1, visibleStartRow * columns),
      );
      const visibleEndIndex = Math.min(
        virtualItemCount,
        Math.max(visibleStartIndex + 1, visibleEndRow * columns),
      );
      const rowsAfter = Math.max(0, rowCount - endRow);
      const topSpacer = startRow > 0 ? startRow * rowStride - rowGap : 0;
      const bottomSpacer = rowsAfter > 0 ? rowsAfter * rowStride - rowGap : 0;
      const totalHeight = rowCount * rowStride - rowGap;
      const nextWindow = {
        columns,
        endRow,
        endIndex,
        itemHeight,
        rowGap,
        rowStride,
        startRow,
        startIndex,
        totalHeight,
        topSpacer,
        bottomSpacer,
        visibleStartIndex,
        visibleEndIndex,
      };
      const hasChanged = (Object.keys(nextWindow) as Array<keyof VirtualWindow>).some((key) => {
        return Math.abs(nextWindow[key] - prevWindow[key]) > 1;
      });

      return hasChanged ? nextWindow : prevWindow;
    });
  }, [shouldVirtualize, virtualItemCount, virtualOverscan]);

  const scheduleVirtualWindowUpdate = useCallback(() => {
    if (virtualFrameRef.current !== null) return;

    virtualFrameRef.current = window.requestAnimationFrame(() => {
      virtualFrameRef.current = null;
      const now = performance.now();
      if (now - virtualLastUpdateRef.current < VIRTUAL_SCROLL_UPDATE_INTERVAL) {
        if (virtualDelayTimerRef.current === null) {
          virtualDelayTimerRef.current = window.setTimeout(() => {
            virtualDelayTimerRef.current = null;
            virtualLastUpdateRef.current = performance.now();
            updateVirtualWindow();
          }, VIRTUAL_SCROLL_UPDATE_INTERVAL);
        }
        return;
      }
      virtualLastUpdateRef.current = now;
      updateVirtualWindow();
    });
  }, [updateVirtualWindow]);

  useLayoutEffect(() => {
    if (!shouldVirtualize) return;

    updateVirtualMetrics();
    updateVirtualWindow();
    window.addEventListener('scroll', scheduleVirtualWindowUpdate, { passive: true });
    const handleResize = () => {
      updateVirtualMetrics();
      scheduleVirtualWindowUpdate();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (virtualFrameRef.current !== null) {
        window.cancelAnimationFrame(virtualFrameRef.current);
        virtualFrameRef.current = null;
      }
      if (virtualDelayTimerRef.current !== null) {
        window.clearTimeout(virtualDelayTimerRef.current);
        virtualDelayTimerRef.current = null;
      }
      window.removeEventListener('scroll', scheduleVirtualWindowUpdate);
      window.removeEventListener('resize', handleResize);
    };
  }, [scheduleVirtualWindowUpdate, shouldVirtualize, updateVirtualMetrics, updateVirtualWindow]);

  useEffect(() => {
    if (isWeb || !shouldVirtualize) return;

    onVirtualRangeChange?.({
      startIndex: virtualWindow.startIndex,
      endIndex: virtualWindow.endIndex,
      visibleStartIndex: virtualWindow.visibleStartIndex,
      visibleEndIndex: virtualWindow.visibleEndIndex,
      pageSize: limit,
      total,
    });

    if (onVirtualRangeChange || loading || loadedArticleCount >= total) return;

    const targetLoadedCount = Math.min(total, virtualWindow.endIndex + limit);
    if (
      targetLoadedCount > loadedArticleCount &&
      virtualWindow.endIndex >= loadedArticleCount - limit
    ) {
      onLoadMore?.(targetLoadedCount);
    }
  }, [
    isWeb,
    limit,
    loadedArticleCount,
    loading,
    onLoadMore,
    onVirtualRangeChange,
    shouldVirtualize,
    total,
    virtualWindow.endIndex,
    virtualWindow.startIndex,
    virtualWindow.visibleEndIndex,
    virtualWindow.visibleStartIndex,
  ]);

  const visibleArticles = useMemo(() => {
    if (!shouldVirtualize || !Array.isArray(articles)) return articles;
    return articles.slice(virtualWindow.startIndex, virtualWindow.endIndex);
  }, [articles, shouldVirtualize, virtualWindow.endIndex, virtualWindow.startIndex]);

  const virtualRows = useMemo(() => {
    if (!shouldVirtualize || !Array.isArray(articles)) return [];

    const rows = [];
    for (let rowIndex = virtualWindow.startRow; rowIndex < virtualWindow.endRow; rowIndex += 1) {
      const startIndex = rowIndex * virtualWindow.columns;
      const rowArticles = Array.from({ length: virtualWindow.columns }, (_, offset) => {
        const index = startIndex + offset;
        if (index >= virtualItemCount) return undefined;
        return articles[index];
      });
      rows.push({
        articles: rowArticles,
        index: rowIndex,
        start: rowIndex * virtualWindow.rowStride,
        startIndex,
      });
    }

    return rows;
  }, [
    articles,
    shouldVirtualize,
    virtualItemCount,
    virtualWindow.columns,
    virtualWindow.endRow,
    virtualWindow.rowStride,
    virtualWindow.startRow,
  ]);

  const renderVirtualPlaceholder = (index: number) => (
    <div
      key={`virtual-placeholder-${index}`}
      aria-hidden="true"
      className={itemClassName}
      style={{
        contentVisibility: 'auto',
        containIntrinsicSize: isWeb ? '520px' : '430px',
        minHeight: virtualWindow.itemHeight,
      }}
    />
  );

  const infiniteScrollPage =
    loadedCount === undefined ? page : Math.max(1, Math.ceil(loadedArticleCount / limit));
  const hasArticleList =
    Array.isArray(articles) && (articles.length > 0 || (shouldVirtualize && total > 0));
  const shouldShowEmptyState =
    Array.isArray(articles) && articles.length === 0 && total <= 0 && !loading;
  const shouldShowLoadingSpacer =
    Array.isArray(articles) && articles.length === 0 && total <= 0 && loading;

  // 动态标题组件
  const TitleComponent = titleComponent;
  return (
    <div
      className={`
      latest-news w-full  min-h-[95vh]
      ${deviceType === EPlatform.WEB ? 'pt-[5.42vw]' : 'px-14 pt-30'}
      ${className}
    `}
      id="article-list"
    >
      {/* 标题 */}
      {title && (
        <div className="title">
          <TitleComponent
            className={`title-text font-bb ${deviceType === EPlatform.WEB ? 'text-32' : 'text-20'} ${titleClassName}`}
          >
            {title}
          </TitleComponent>
        </div>
      )}

      {/* 文章列表 */}
      {hasArticleList && (
        <ul
          ref={listRef}
          className={`
            news-list
            ${deviceType === EPlatform.WEB ? 'mt-[1.67vw] grid grid-cols-3 gap-x-[24px] gap-y-[64px] [@media(max-width:1540px)]:grid-cols-2 [@media(max-width:960px)]:grid-cols-1' : 'mt-16'}
            ${shouldVirtualize ? '!block relative' : ''}
            ${listClassName}
          `}
          style={shouldVirtualize ? { height: virtualWindow.totalHeight } : undefined}
        >
          {shouldVirtualize
            ? virtualRows.map((row) => (
                <li
                  key={`virtual-row-${row.index}`}
                  className={`
                    absolute left-0 top-0 w-full
                    ${isWeb ? 'grid grid-cols-3 gap-x-[24px] [@media(max-width:1540px)]:grid-cols-2 [@media(max-width:960px)]:grid-cols-1' : ''}
                  `}
                  style={{
                    contain: 'layout paint',
                    minHeight: virtualWindow.itemHeight,
                    transform: `translate3d(0, ${row.start}px, 0)`,
                    willChange: 'transform',
                  }}
                >
                  {row.articles.map((article, rowOffset) => {
                    const index = row.startIndex + rowOffset;

                    if (index >= virtualItemCount) return null;
                    if (!article) return renderVirtualPlaceholder(index);

                    return (
                      <ArticleListItem
                        key={article.id}
                        as="div"
                        article={article}
                        articleClassName={articleClassName}
                        index={index}
                        isWeb={isWeb}
                        itemClassName={itemClassName}
                        listExp={listExp}
                        listItemClick={listItemClick}
                        useNativeArticleImage={useNativeArticleImage}
                      />
                    );
                  })}
                </li>
              ))
            : visibleArticles.map((article, visibleIndex) => {
                if (!article) return null;
                const index = shouldVirtualize
                  ? virtualWindow.startIndex + visibleIndex
                  : visibleIndex;

                return (
                  <ArticleListItem
                    key={article.id}
                    article={article}
                    articleClassName={articleClassName}
                    index={index}
                    isWeb={isWeb}
                    itemClassName={itemClassName}
                    listExp={listExp}
                    listItemClick={listItemClick}
                    useNativeArticleImage={useNativeArticleImage}
                  />
                );
              })}
        </ul>
      )}
      {shouldShowEmptyState && (emptyComponent || <BlogNoArticle />)}
      {shouldShowLoadingSpacer && <div className="h-[60px]" />}
      <WapOnly>
        <InfiniteScroll
          onReachBottom={handleReachBottom}
          loading={loading}
          page={infiniteScrollPage}
          total={total}
          limit={limit}
        />
      </WapOnly>
      <WebOnly>
        {total > limit && (
          <Pagination
            current={page}
            total={total}
            pageSize={limit}
            onChange={handlePageChange}
            className="mt-[2.6vw] pb-[64px]"
            sx={{
              '& .Mui-disabled': {
                color: '#28282822 !important',
                backgroundColor: 'transparent',
              },
            }}
          />
        )}
      </WebOnly>
    </div>
  );
};

export default ArticleList;
